import React, { useState } from "react";

import { Button } from "components/common/Button";
import { InlineError } from "components/common/InlineError";
import {
  type ConnectionType,
  type SavedConnection,
} from "components/canvas/leftSide/import/Postgres/savedConnectionsApi";
import { useMintOrgId } from "components/main/connections/useMintOrg";
import { useConnectionsStore } from "stores/connectionsStore";
import {
  caCertForSubmit,
  Field,
  MAX_NAME_LEN,
  PgCredentialFields,
  SslSection,
  type PgFormState,
} from "components/main/utils/settings/connectionForm";

const emptyForm = (): PgFormState => ({
  display_name: "",
  host: "",
  port: 5432,
  database: "",
  username: "",
  password: "",
  ssl_mode: "VerifyFull",
  ca_cert_pem: "",
  clear_ca_cert: false,
});

const fromExisting = (c: SavedConnection): PgFormState => {
  // Non-Postgres-like variants are routed to their own modals by
  // ConnectionEditModal — this guard is defence-in-depth for type narrowing.
  if (
    c.credentials.type === "google_oauth" ||
    c.credentials.type === "bucket" ||
    c.credentials.type === "local_sqlite"
  ) {
    return emptyForm();
  }
  const cr = c.credentials;
  return {
    display_name: c.display_name,
    host: cr.host,
    port: cr.port,
    database: cr.database,
    username: cr.username,
    password: "",
    ssl_mode: cr.ssl_mode,
    ca_cert_pem: "",
    clear_ca_cert: false,
  };
};

interface Props {
  // When set, the form prefills all fields except password (server never returns it).
  existing?: SavedConnection;
  // Type for new connections. Ignored on edit. Defaults to "postgres".
  newConnectionType?: ConnectionType;
  onSaved: (saved: SavedConnection) => void;
  onCancel: () => void;
  // Hide the in-form Cancel button — used by NewConnectionPanel, which
  // owns its own back arrow in the panel header.
  hideCancel?: boolean;
}

export const SavedConnectionEditor: React.FC<Props> = ({
  existing,
  newConnectionType = "postgres",
  onSaved,
  onCancel,
  hideCancel = false,
}) => {
  const isEdit = !!existing;
  const mintOrgId = useMintOrgId();
  const [form, setForm] = useState<PgFormState>(
    existing ? fromExisting(existing) : emptyForm(),
  );
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const existingHasCaCert =
    isEdit &&
    existing?.credentials.type !== "google_oauth" &&
    existing?.credentials.type !== "bucket" &&
    existing?.credentials.type !== "local_sqlite" &&
    !!existing?.credentials.has_ca_cert_pem;

  const baseReady =
    form.display_name.trim().length > 0 &&
    form.host.length > 0 &&
    form.database.length > 0 &&
    form.username.length > 0 &&
    form.port > 0;
  const ready = isEdit ? baseReady : baseReady && form.password.length > 0;

  const update = (patch: Partial<PgFormState>) =>
    setForm((prev) => ({ ...prev, ...patch }));

  const handleSubmit = async () => {
    setPending(true);
    setError(null);
    try {
      const cert = caCertForSubmit(form);
      let saved: SavedConnection;
      if (existing) {
        saved = await useConnectionsStore.getState().update(existing.id, {
          display_name: form.display_name.trim(),
          // Empty password leaves the existing one intact server-side.
          password: form.password.length > 0 ? form.password : undefined,
          ssl_mode: form.ssl_mode,
          ca_cert_pem: cert,
          // clear_ca_cert and ca_cert_pem are mutually exclusive server-side.
          clear_ca_cert: cert === undefined ? form.clear_ca_cert : false,
        });
      } else {
        saved = await useConnectionsStore.getState().create({
          display_name: form.display_name.trim(),
          host: form.host,
          port: form.port,
          database: form.database,
          username: form.username,
          password: form.password,
          ssl_mode: form.ssl_mode,
          ca_cert_pem: cert,
          connection_type:
            newConnectionType === "supabase" ? "supabase" : "postgres",
          org_id: mintOrgId,
        });
      }
      onSaved(saved);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to save connection");
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="flex flex-col gap-3">
      {error && <InlineError>{error}</InlineError>}
      <Field
        label="Name"
        value={form.display_name}
        onChange={(v) => update({ display_name: v.slice(0, MAX_NAME_LEN) })}
        placeholder="Prod readonly"
      />
      <PgCredentialFields
        form={form}
        update={update}
        disabled={isEdit}
        hostPlaceholder="postgres.example.com"
        passwordLabel={
          isEdit ? "New password (leave blank to keep current)" : "Password"
        }
      />
      <SslSection
        form={form}
        update={update}
        showClearCaToggle={existingHasCaCert}
      />
      <div className="flex justify-end gap-2 mt-2">
        {!hideCancel && (
          <Button
            variant="secondary"
            size="md"
            onClick={onCancel}
            disabled={pending}
          >
            Cancel
          </Button>
        )}
        <Button
          variant="primary"
          size="md"
          loading={pending}
          disabled={!ready}
          onClick={handleSubmit}
        >
          {isEdit ? "Save" : "Test & save"}
        </Button>
      </div>
    </div>
  );
};
